import { ApiError, NetworkError } from './errors';
import { failUnlessUnloading, installUnloadGuard } from './unload-guard';

installUnloadGuard();
import { readCsrf } from './csrf';
import { addBreadcrumb, captureApiError, captureError, markReported, newTraceId } from '@/lib/telemetry';
import { API_BASE as BASE } from './base';
import { MAINTENANCE_CODE, clearMaintenance, noteMaintenance } from '@/lib/maintenance';

/**
 * Route a third-party image (e.g. an EncorePro poster on telecharge.com) through
 * the API's `/images/proxy`, which fetches it server-side and strips all
 * upstream `Set-Cookie` headers. Same-origin to the worker; the worker enforces
 * an upstream-host allowlist.
 */
export function imageProxyUrl(src: string): string {
  return `${BASE}/images/proxy?url=${encodeURIComponent(src)}`;
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface CallOptions {
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export interface CallResult<R> {
  data: R;
  requestId: string | null;
  /** Server cache/revision provenance. These values are operational metadata,
   * never entity identifiers or response content. */
  samCache: string | null;
  samDataRevision: string | null;
  samGeneratedAt: string | null;
  /** The trace id this request was sent with. Unlike `requestId` (read off the
   *  response) this is known even when the request never got a response, and it
   *  is what a caller reuses to tie follow-up work to the same trace. */
  traceId: string;
  /** e.g. "POST /campaigns" — useful for the ProgressModal sub-line. */
  endpoint: string;
}

let on401: (() => void) | null = null;
let forcedRefreshDepth = 0;

/**
 * Marks all API reads started by `operation` as an operator-forced refresh.
 *
 * Query functions do not accept arbitrary per-refetch headers, so the global
 * refresh coordinator scopes the header at the API-client boundary. The depth
 * counter keeps nested refresh work balanced. Requests merely overlapping in
 * time are uncommon, but sending the idempotent force hint to one is safe.
 */
export async function withForcedApiRefresh<T>(operation: () => Promise<T>): Promise<T> {
  forcedRefreshDepth += 1;
  try {
    return await operation();
  } finally {
    forcedRefreshDepth -= 1;
  }
}

// Review-link tokens travel in the JSON body, never the path, so breadcrumbs and
// error reports carry no credential. This strips a token anyway in case a future
// route puts one in the URL — belt and braces, not the current shape.
function telemetryPath(path: string): string {
  return path.replace(/[A-Fa-f0-9]{64}/g, '[token]');
}

export function setUnauthorizedHandler(handler: () => void): void {
  on401 = handler;
}

async function callRaw<R>(
  method: Method,
  path: string,
  opts: CallOptions = {},
): Promise<CallResult<R>> {
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };
  const hasBody = opts.body !== undefined;
  // A Blob body ships raw — asset upload parts, where JSON.stringify would
  // silently serialize the Blob to "{}" and lose the bytes.
  const isBinary = opts.body instanceof Blob;

  if (hasBody) {
    headers['Content-Type'] = isBinary ? 'application/octet-stream' : 'application/json';
  }
  if (method !== 'GET') {
    const csrf = readCsrf();
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }

  // Mint a per-request trace id. The API echoes it into any log line it
  // writes (via X-Request-Id), so a failed call correlates across frontend +
  // worker. A caller may supply its own to group several requests — and the
  // browser-direct R2 upload lane — under one trace.
  const traceId = headers['X-Request-Id'] ?? newTraceId();
  headers['X-Request-Id'] = traceId;
  if (forcedRefreshDepth > 0 && headers['X-SAM-Refresh'] === undefined) {
    headers['X-SAM-Refresh'] = 'force';
  }

  const url = `${BASE}${path.startsWith('/') ? path : `/${path}`}`;
  const safePath = telemetryPath(path);
  // Telemetry's own POST /telemetry uses raw fetch (not this client), so it
  // never breadcrumbs or recurses through here.
  addBreadcrumb('api', `${method} ${safePath}`);

  let res: globalThis.Response;
  try {
    res = await fetch(url, {
      method,
      credentials: 'include',
      // TanStack Query is the only response cache. Browser HTTP caches cannot
      // see domain revisions and would otherwise replay an older generation
      // after the coordinator deliberately refetched.
      cache: 'no-store',
      headers,
      body: hasBody ? (isBinary ? (opts.body as Blob) : JSON.stringify(opts.body)) : undefined,
      signal: opts.signal,
    });
  } catch (err) {
    // An abort is not a failure — it is the caller navigating away or
    // react-query cancelling, and it must keep its `AbortError` identity so the
    // existing checks for it still match.
    if (err instanceof DOMException && err.name === 'AbortError') throw markReported(err);
    // Everything else that comes out of `fetch` is a network failure, and says
    // so in its type rather than leaving every catch site to guess from a bare
    // `TypeError` — unless the page is unloading, when WebKit kills in-flight
    // requests with the same `TypeError` (unload-guard.ts).
    return failUnlessUnloading(() => {
      // Fire-and-forget: probing must not delay the rejection the caller is
      // waiting on, and a probe that itself fails must not replace the real
      // error. The throw below happens either way.
      void classifyApiFailure()
        .then(({ kind, evidence }) =>
          captureApiError({
            method,
            path: safePath,
            status: null,
            trace_id: traceId,
            message: String((err as Error)?.message ?? err),
            error_kind: kind,
            context: evidence,
          }),
        )
        .catch(() =>
          captureApiError({
            method,
            path: safePath,
            status: null,
            trace_id: traceId,
            message: String((err as Error)?.message ?? err),
          }),
        );
      throw markReported(new NetworkError(err));
    });
  }

  const requestId = res.headers.get('X-Request-Id');
  const samCache = res.headers.get('X-SAM-Cache');
  const samDataRevision = res.headers.get('X-SAM-Data-Revision');
  const samGeneratedAt = res.headers.get('X-SAM-Generated-At');
  const endpoint = `${method} ${safePath}`;

  // Record provenance before status handling so an error log's breadcrumb
  // trail still says which cache/revision generation produced the response.
  if (samCache !== null || samDataRevision !== null || samGeneratedAt !== null) {
    addBreadcrumb(
      'data',
      `${endpoint} cache=${samCache ?? '-'} revision=${samDataRevision ?? '-'} generated=${samGeneratedAt ?? '-'}`,
    );
  }

  if (res.status === 401) {
    // Capture before `on401` — it navigates to /login, and this used to return
    // here without reporting anything, which made `captureApiError`'s
    // `session_expired` branch unreachable in practice. No method check: that
    // branch already stays silent for GET/HEAD (an idle-session redirect), so
    // the one deliberate silence lives in one place.
    const apiErr = await ApiError.fromResponse(res);
    captureApiError({ method, path: safePath, status: 401, trace_id: traceId, message: apiErr.message, code: apiErr.code });
    on401?.();
    throw markReported(apiErr);
  }

  if (!res.ok) {
    const apiErr = await ApiError.fromResponse(res);
    // Changes are paused while SMART moves hosts: the banner says so once, app-wide.
    if (res.status === 503 && apiErr.code === MAINTENANCE_CODE) noteMaintenance();
    captureApiError({
      method, path: safePath, status: res.status, trace_id: traceId, message: apiErr.message, code: apiErr.code,
    });
    throw markReported(apiErr);
  }

  // A change went through, so whatever paused changes is over.
  if (method !== 'GET') clearMaintenance();

  if (res.status === 204) {
    return {
      data: undefined as R,
      requestId,
      traceId,
      endpoint,
      samCache,
      samDataRevision,
      samGeneratedAt,
    };
  }

  const contentType = res.headers.get('Content-Type') ?? '';
  const data = contentType.includes('application/json')
    ? ((await res.json()) as R)
    : ((await res.text()) as unknown as R);
  return {
    data,
    requestId,
    traceId,
    endpoint,
    samCache,
    samDataRevision,
    samGeneratedAt,
  };
}

/**
 * Low-level variant that exposes the `X-Request-Id` response header and
 * a synthesized `endpoint` ("POST /campaigns") for the ProgressModal
 * per-step technical sub-line. Other callers should keep using `api.*`.
 */
export function apiWithMeta<R>(
  method: Method,
  path: string,
  opts?: CallOptions,
): Promise<CallResult<R>> {
  return callRaw<R>(method, path, opts);
}

export const api = {
  get: <R>(path: string, opts?: CallOptions) =>
    callRaw<R>('GET', path, opts).then((r) => r.data),
  post: <R>(path: string, opts?: CallOptions) =>
    callRaw<R>('POST', path, opts).then((r) => r.data),
  put: <R>(path: string, opts?: CallOptions) =>
    callRaw<R>('PUT', path, opts).then((r) => r.data),
  patch: <R>(path: string, opts?: CallOptions) =>
    callRaw<R>('PATCH', path, opts).then((r) => r.data),
  delete: <R>(path: string, opts?: CallOptions) =>
    callRaw<R>('DELETE', path, opts).then((r) => r.data),
  /** Raw-body PUT for asset upload parts: same csrf/session/telemetry plumbing,
   *  body shipped as application/octet-stream instead of JSON. */
  putBinary: <R>(path: string, body: Blob, opts?: Omit<CallOptions, 'body'>) =>
    callRaw<R>('PUT', path, { ...opts, body }).then((r) => r.data),
};

/**
 * Recovery/preflight read that deliberately bypasses Query state. It retains
 * the normal authenticated client, CSRF/trace and telemetry behavior.
 */
export function networkOnlyRead<R>(path: string, opts?: CallOptions): Promise<R> {
  return callRaw<R>('GET', path, opts).then((result) => result.data);
}

// ── the published release ────────────────────────────────────────────────────

/** How long a `version.json` read may take before it counts as unreadable. */
const RELEASE_READ_TIMEOUT_MS = 10_000;

/**
 * The release Pages serves now (`/version.json`, same origin), checked by `parse`, or null.
 *
 * Silent on purpose: a deploy window can answer 404 or the SPA's index.html, a laptop waking
 * up has no network yet, and none of that is a fault anyone acts on. So no breadcrumb, no
 * telemetry and no unload guard; the caller simply reads again later (src/lib/app-release.ts,
 * 2026-10-21).
 */
export async function readPublishedRelease<T>(parse: (json: unknown) => T | null): Promise<T | null> {
  try {
    const res = await fetch('/version.json', {
      cache: 'no-store',
      credentials: 'same-origin',
      signal: AbortSignal.timeout(RELEASE_READ_TIMEOUT_MS),
    });
    if (!res.ok) return null;
    return parse(await res.json());
  } catch {
    return null;
  }
}

// ── external (non-API) requests ──────────────────────────────────────────────

/** How long the failure-path reachability probes may take. Never runs on the
 *  success path. */
const PROBE_TIMEOUT_MS = 2_000;

async function reachable(url: string, mode: RequestMode): Promise<boolean> {
  try {
    await fetch(url, { mode, cache: 'no-store', signal: AbortSignal.timeout(PROBE_TIMEOUT_MS) });
    return true;
  } catch {
    return false;
  }
}

/**
 * Classify a failed cross-origin request.
 *
 * A rejected cross-origin `fetch` exposes **nothing** to JavaScript — no status,
 * no headers, no cause. That is deliberate in the Fetch spec: revealing the
 * reason would let a page probe the cross-origin network. The rejection message
 * is engine-specific ("NetworkError when attempting to fetch resource" on
 * Firefox, "Failed to fetch" on Chrome, "Load failed" on Safari) and must never
 * be pattern-matched. Chrome prints the real cause to the devtools console; that
 * string is unreachable from JS.
 *
 * So this is inference, not proof. We corroborate with two cheap probes and
 * attach the raw evidence to the log line so an operator can overrule the
 * verdict. Known ways it can mislead: a captive portal or TLS-inspecting proxy,
 * and an ad blocker that blocks the no-cors probe (reads as unreachable).
 */
async function classifyNetworkFailure(
  target: string,
  kindPrefix: string,
): Promise<{ kind: string; evidence: Record<string, unknown> }> {
  const online = typeof navigator === 'undefined' ? true : navigator.onLine !== false;
  if (!online) {
    return { kind: `${kindPrefix}_offline`, evidence: { online: false } };
  }
  const started = Date.now();
  let host = target;
  try {
    host = new URL(target).origin;
  } catch {
    /* keep the raw string */
  }
  const [hostReachable, apiReachable] = await Promise.all([
    reachable(host, 'no-cors'),
    reachable(`${BASE}/healthz`, 'cors'),
  ]);
  const evidence = {
    online,
    host_reachable: hostReachable,
    api_reachable: apiReachable,
    probe_ms: Date.now() - started,
  };
  // Host reachable ⇒ DNS, TCP and TLS all succeeded, so the transport is fine
  // and the browser refused the response — overwhelmingly a CORS rejection.
  // API reachable but host not ⇒ the network is up and only this origin failed,
  // which is the same conclusion by a different route.
  const corsLikely = hostReachable || apiReachable;
  return { kind: corsLikely ? `${kindPrefix}_cors` : `${kindPrefix}_network`, evidence };
}

/**
 * Classify a failed request to our own API.
 *
 * `classifyNetworkFailure` above is for cross-origin targets and corroborates
 * with two probes — the target's origin and ours. On this path those are the
 * same host, so `hostReachable || apiReachable` would be near-tautological and
 * every failure would read as CORS. What is actually worth knowing here is
 * narrower: is the API reachable at all right now?
 *
 * That distinction is the whole point. A week of production logs contained 31
 * identical `NetworkError when attempting to fetch resource` lines, including
 * seven *different* routes failing in the same second — an outage — recorded no
 * differently from one user's connection dropping mid-request. The engine string
 * is unusable for this (it is `Failed to fetch` on Chrome, `Load failed` on
 * Safari, and must never be pattern-matched), so ask the network instead.
 *
 * Inference, not proof — same caveats as `classifyNetworkFailure`: a captive
 * portal, a TLS-inspecting proxy, or an ad blocker that eats the probe all read
 * as unreachable. The evidence rides along on the log line so an operator can
 * overrule the verdict.
 */
async function classifyApiFailure(): Promise<{ kind: string; evidence: Record<string, unknown> }> {
  const online = typeof navigator === 'undefined' ? true : navigator.onLine !== false;
  if (!online) {
    return { kind: 'api_offline', evidence: { online: false } };
  }
  const started = Date.now();
  const apiReachable = await reachable(`${BASE}/healthz`, 'cors');
  return {
    // Reachable ⇒ the edge is up and only this request died: an aborted
    // connection, a TLS blip, an extension. Unreachable ⇒ the API, DNS or CORS
    // is broken for everyone, and a burst of these is one incident, not many.
    kind: apiReachable ? 'api_transient' : 'api_unreachable',
    evidence: { online, api_reachable: apiReachable, probe_ms: Date.now() - started },
  };
}

export interface ExternalFetchMeta {
  /** Short stable label for the breadcrumb, e.g. "r2 part 1/8". */
  label: string;
  /** Ties this request to the API requests around it. */
  traceId: string;
  /** Prefix for the emitted error_kind, e.g. "r2_upload". */
  kindPrefix: string;
  context?: Record<string, unknown>;
}

/**
 * The only sanctioned way to fetch something that is not the API.
 *
 * A bare `fetch` to a third-party origin produces no log line anywhere: it never
 * reaches the Worker, so there is no server-side record, and if the caller
 * catches the throw it is not an unhandled rejection either. That is how a
 * completely broken upload path stayed invisible. `network.guard.test.ts`
 * enforces that this is the only such call site.
 *
 * Rethrows the original error unchanged, so caller control flow is untouched.
 */
export async function fetchExternal(
  url: string,
  init: RequestInit,
  meta: ExternalFetchMeta,
): Promise<globalThis.Response> {
  addBreadcrumb('net', meta.label);
  try {
    const res = await fetch(url, { ...init, cache: 'no-store' });
    if (!res.ok) {
      captureError({
        level: 'error',
        error_kind: `${meta.kindPrefix}_rejected`,
        message: `${meta.kindPrefix}_rejected: ${meta.label} → ${res.status} ${res.statusText}`,
        http_status: res.status,
        trace_id: meta.traceId,
        context: meta.context,
      });
    }
    return res;
  } catch (error) {
    // An abort is a user action, not a fault. Record it so a cancel is
    // distinguishable from a failure — server-side both look like a successful
    // upload-abort — but keep it out of the error counts.
    if (error instanceof DOMException && error.name === 'AbortError') {
      captureError({
        level: 'info',
        error_kind: `${meta.kindPrefix}_aborted`,
        message: `${meta.kindPrefix}_aborted: ${meta.label} cancelled`,
        trace_id: meta.traceId,
        context: meta.context,
      });
      throw error;
    }
    const { kind, evidence } = await classifyNetworkFailure(url, meta.kindPrefix);
    captureError({
      level: kind.endsWith('_offline') ? 'warn' : 'error',
      error_kind: kind,
      message: `${kind}: ${meta.label} could not be sent`,
      trace_id: meta.traceId,
      context: { ...(meta.context ?? {}), ...evidence },
    });
    throw error;
  }
}
